import { useCallback, useEffect, useSyncExternalStore } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  VALUE_PRIORITIES_ACCOUNT_KEY,
  getServerValuePriorities,
  getValuePriorities,
  sanitiseValuePriorities,
  setValuePriorities,
  subscribeToValuePriorities,
} from '../util/valuePriorities';

/**
 * Save the shopper's priorities to their private profile data.
 *
 * Best effort: they're already saved on this device, so a failed request only
 * means they won't follow the shopper to another device this time.
 *
 * @param {Array<string>} priorities ecolabel keys
 */
export const saveValuePrioritiesToAccount = priorities => (dispatch, getState, sdk) =>
  sdk.currentUser
    .updateProfile({ privateData: { [VALUE_PRIORITIES_ACCOUNT_KEY]: priorities } })
    .catch(() => null);

// The account value last copied to this device, so it's copied once per change
// rather than overwriting a newer local edit on every render.
let adoptedAccountSignature = null;

const accountSignatureOf = (userId, priorities) =>
  userId && Array.isArray(priorities) ? `${userId}:${priorities.join(',')}` : null;

/**
 * The shopper's priorities (ecolabel keys). Safe anywhere, including outside a
 * Redux Provider: it only reads the device store. Empty on the server and during
 * hydration, so server and client render the same markup.
 *
 * @returns {Array<string>}
 */
export const useValuePriorities = () =>
  useSyncExternalStore(subscribeToValuePriorities, getValuePriorities, getServerValuePriorities);

/**
 * Read and change the shopper's priorities. Changes are saved on this device
 * straight away, and to the shopper's account too when they're logged in.
 *
 * @returns {{priorities: Array<string>, setPriorities: Function, isLoggedIn: boolean}}
 */
export const useValuePrioritiesEditor = () => {
  const priorities = useValuePriorities();
  const dispatch = useDispatch();
  const userId = useSelector(state => state.user?.currentUser?.id?.uuid);

  const setPriorities = useCallback(
    values => {
      const saved = setValuePriorities(values);
      if (userId) {
        adoptedAccountSignature = accountSignatureOf(userId, saved);
        dispatch(saveValuePrioritiesToAccount(saved));
      }
      return saved;
    },
    [dispatch, userId]
  );

  return { priorities, setPriorities, isLoggedIn: !!userId };
};

/**
 * Copies the priorities saved in the shopper's account onto this device when
 * they log in, so the same priorities follow them between devices. Rendered once,
 * near the root of the app.
 *
 * @returns {null}
 */
export const ValuePrioritiesAccountSync = () => {
  const userId = useSelector(state => state.user?.currentUser?.id?.uuid);
  const accountPriorities = useSelector(
    state =>
      state.user?.currentUser?.attributes?.profile?.privateData?.[VALUE_PRIORITIES_ACCOUNT_KEY]
  );
  const signature = accountSignatureOf(userId, accountPriorities);

  useEffect(() => {
    if (signature && signature !== adoptedAccountSignature) {
      adoptedAccountSignature = signature;
      setValuePriorities(sanitiseValuePriorities(accountPriorities));
    }
  }, [signature]); // eslint-disable-line react-hooks/exhaustive-deps

  return null;
};
